import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Box, Drawer, Collapse, Typography, Paper } from '@mui/material';
import TopNavBar from './TopNavBar';
import LeftSidebar from './LeftSidebar';
import ContextBar from './ContextBar';
import ModeIndicator from './ModeIndicator';
import ViewTitleBar from './ViewTitleBar';
import ColumnDetailPanel from './ColumnDetailPanel';
import DynamicForm from './DynamicForm';
import CoverageMatrix from './CoverageMatrix';
import NewViewDialog from './NewViewDialog';
import ViewSubTabs, { ViewSubTab } from './ViewSubTabs';
import ModelsPanel from './ModelsPanel';
import PivotsPanel from './PivotsPanel';
import { useApiClient } from '../../config/axios';
import ChatbotPanel from '../Chatbot/ChatbotPanel';

// TypeScript interfaces
interface NavigationRow {
  topNav: string;
  leftNav: string;
  viewLabel: string;
  configName: string;
  viewKey: string;
  topDown: boolean;
  bottomUp: boolean;
  viewType?: string | null;
  itemCount?: number | string | null;
}

interface ViewItem {
  item_id: string;
  parent_item_id?: string | null;
  data_index?: string;
  label_text?: string;
  renderer?: string;
  width?: number | null;
  pinned?: number | null;
  seq_no?: number;
  role?: string;
  extra_props?: Record<string, any>;
  _isNew?: boolean;
  [key: string]: any;
}

/* ───────── helpers for preview/export ────────── */

/** Merge extra_props into the row itself and drop DB bookkeeping columns.
 *
 * Per the DB-contract: a standard field is omitted from the output JSON ONLY
 * when its column is NULL. Empty string, 0, and false are valid values that
 * must round-trip verbatim (e.g. `"text": ""` for a column with no header
 * text). The OLD shorthand `label_text ? ... : {}` dropped empty strings,
 * `width ? ...` dropped 0, etc. Bug 2's "editable: false" loss had a related
 * cause inside cleanObject; this is the row-level half of the same problem.
 */
function normaliseRow(r: ViewItem) {
  const {
    item_id, parent_item_id,          // keep for tree building
    data_index, label_text, renderer, width, pinned,
    extra_props,
    // dbJunk left destructured-out: created_at, updated_at, locked, …
    ...rest                           // eslint-disable-line @typescript-eslint/no-unused-vars
  } = r as any;

  // extra_props may arrive as a parsed object (postgres `json` column) or as
  // a raw string (legacy paths / Excel import). Parse once if string.
  let parsedExtraProps: Record<string, any> = {};
  if (extra_props) {
    if (typeof extra_props === 'string') {
      try {
        parsedExtraProps = JSON.parse(extra_props);
      } catch (e) {
        console.warn('Failed to parse extra_props JSON:', extra_props);
        parsedExtraProps = {};
      }
    } else if (typeof extra_props === 'object') {
      parsedExtraProps = extra_props;
    }
  }

  // Build the merged object. Spread extra_props first so its key order is
  // preserved; standard fields get spliced in after. ONLY NULL/undefined
  // suppress a field — false, "", and 0 must survive.
  const out: Record<string, any> = { ...parsedExtraProps };
  if (label_text !== null && label_text !== undefined) out.text = label_text;
  if (data_index !== null && data_index !== undefined) out.dataIndex = data_index;
  if (renderer !== null && renderer !== undefined) out.renderer = renderer;
  if (width !== null && width !== undefined) out.width = width;
  if (pinned !== null && pinned !== undefined) out.pinned = pinned;
  out.item_id = item_id;
  out.parent_item_id = parent_item_id;
  return out;
}

/** build the nested `.view` array exactly like
 *  the original *.viewdefn* files
 */
function buildView(items: ViewItem[]) {
  const byId = new Map();          // item_id -> node
  const roots: any[] = [];                 // top‑level nodes

  // 1/ create a plain JS node for each row
  items.forEach(r => {
    const node = normaliseRow(r);
    (node as any).view = [];                  // space for children
    byId.set(node.item_id, node);
  });

  // 2/ link children to their parents
  items.forEach(r => {
    const node = byId.get(r.item_id);
    if (r.parent_item_id && byId.has(r.parent_item_id)) {
      byId.get(r.parent_item_id).view.push(node);
    } else {
      roots.push(node);
    }
  });

  // 3/ prune empty "view" arrays
  const prune = (n: any) => {
    // if there's no view array (or it's not an array), skip
    if (!Array.isArray(n.view)) return;
    if (n.view.length === 0) {
      delete n.view;
    } else {
      n.view.forEach(prune);
    }
  };
  roots.forEach(prune);
  return roots;
}

/** Strip ONLY undefined values (which appear during in-memory tree building).
 *
 * Previous version stripped false / empty-string / empty-array / empty-object
 * too — that was the cause of Bug 2 (`"editable": false` disappearing from
 * rendered JSON). Per the DB-contract: emit extra_props verbatim. The only
 * legitimate omission is `undefined` (a value we never wrote, and that JSON
 * has no representation for anyway). Key order is preserved by iterating
 * Object.keys in the order V8 returns them — which matches insertion order
 * for non-integer-key string properties, which all viewdefn keys are.
 */
function cleanObject(o: any): any {
  if (Array.isArray(o)) {
    return o.map(cleanObject).filter(v => v !== undefined);
  }
  if (o && typeof o === 'object') {
    const obj: any = {};
    for (const k of Object.keys(o)) {
      const cv = cleanObject(o[k]);
      if (cv !== undefined) obj[k] = cv;
    }
    return obj;
  }
  return o;
}

/** Build the final viewdefn JSON, honoring the DB contract:
 *   - rootProps keys come first, in stored source order
 *   - `type` appended ONLY if viewType is non-null (Bug 4 — no fabricated 'grid'/'other')
 *   - the column-tree array is placed under the key recorded in nodeField
 *     ('view' | 'columns' | 'main.view' | 'main.columns'). NULL falls back
 *     to 'view' for legacy pre-migration rows.
 *   - empty tree means no column key is emitted at all (config-only views).
 */
function buildViewdefnJson(
  rootProps: Record<string, any>,
  viewType: string | null | undefined,
  nodeField: string | null | undefined,
  tree: any[] | null | undefined
): Record<string, any> {
  // Spread rootProps first to preserve its source key order.
  const result: Record<string, any> = { ...(rootProps || {}) };

  if (viewType !== null && viewType !== undefined) {
    result.type = viewType;
  }

  if (tree && tree.length > 0) {
    const key = nodeField || 'view';   // NULL → 'view' (legacy fallback)
    if (key === 'main.view') {
      result.main = result.main && typeof result.main === 'object' ? { ...result.main } : {};
      result.main.view = tree;
    } else if (key === 'main.columns') {
      result.main = result.main && typeof result.main === 'object' ? { ...result.main } : {};
      result.main.columns = tree;
    } else if (key === 'columns') {
      result.columns = tree;
    } else {
      result.view = tree;
    }
  }
  return result;
}

function ViewManager() {
  const apiClient = useApiClient();
  // State for CSV ui_config and the view definition JSON.
  const [uiConfig, setUIConfig] = useState<NavigationRow[]>([]);
  const [viewData, setViewData] = useState<ViewItem[] | null>(null);

  // Navigation state for context, top nav, left nav, and config selection.
  //
  // Persisted to localStorage per-customer so refresh keeps the user on the
  // view they were editing (instead of dropping back to the empty "Select a
  // view" home). The cascading-reset useEffects below have first-mount guards
  // (topFirst / leftFirst / viewFirst) that skip the wipe on the initial
  // render, so restoring values via useState initializers is safe.
  const persistedInitial = (() => {
    try {
      const customer = JSON.parse(localStorage.getItem('currentCustomer') || 'null');
      if (!customer?.tenant || !customer?.environment) return null;
      const raw = localStorage.getItem(`viewManagerSelection_v1_${customer.tenant}:${customer.environment}`);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  })();
  const [selectedTopNav, setSelectedTopNav] = useState(persistedInitial?.topNav || "");
  const [activeSubTab, setActiveSubTab] = useState<ViewSubTab>('columns');
  const [selectedLeftNav, setSelectedLeftNav] = useState(persistedInitial?.leftNav || "");
  const [selectedView, setSelectedView] = useState(persistedInitial?.view || "");
  const [selectedConfig, setSelectedConfig] = useState(persistedInitial?.config || "");
  useEffect(() => {
    try {
      const customer = JSON.parse(localStorage.getItem('currentCustomer') || 'null');
      if (!customer?.tenant || !customer?.environment) return;
      localStorage.setItem(
        `viewManagerSelection_v1_${customer.tenant}:${customer.environment}`,
        JSON.stringify({
          topNav: selectedTopNav,
          leftNav: selectedLeftNav,
          view: selectedView,
          config: selectedConfig,
        })
      );
    } catch { /* storage unavailable — fail quiet */ }
  }, [selectedTopNav, selectedLeftNav, selectedView, selectedConfig]);

  // New state: planning option (as selected via planning options checkboxes).
  const [isTopDown, setIsTopDown] = useState(false);
  const [isBottomUp, setIsBottomUp] = useState(false);

  // New state: derived view name.
  const [derivedViewName, setDerivedViewName] = useState("");

  // State to track unsaved changes.
  const [unsavedData, setUnsavedData] = useState<ViewItem[] | null>(null);
  const [unsavedTdOnlyMap, setUnsavedTdOnlyMap] = useState<Record<string, boolean>>({});
  const [isDirty, setIsDirty] = useState(false);
  const [rootProps, setRootProps] = useState<Record<string, any>>({});

  // State for optimistic locking
  const [lastModifiedAt, setLastModifiedAt] = useState<string | null>(null);
  // viewType is the source viewdefn's literal `type` field, or null if the
  // source had none. NEVER "" or 'grid' fabrications (those reintroduce Bug 4).
  const [viewType, setViewType] = useState<string | null>(null);
  // nodeField records which source key held the column array:
  //   'view' | 'columns' | 'main.view' | 'main.columns' | null (legacy = 'view')
  const [nodeField, setNodeField] = useState<string | null>(null);
  const [unsavedRootProps, setUnsavedRootProps] = useState<Record<string, any> | null>(null);
  const allowTDOnly = isTopDown;
  const [itemCatalog, setItemCatalog] = useState<Record<string, any>>({});

  // OCI sync state
  const [ociAvailable, setOciAvailable] = useState(false);
  const [ociSyncing, setOciSyncing] = useState(false);

  // Excel download/upload state
  const [uploadingExcel, setUploadingExcel] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Chatbot state
  const [chatOpen, setChatOpen] = useState(false);

  // NEW layout state
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'json' | 'preview'>('grid');
  const [rootPropsExpanded, setRootPropsExpanded] = useState(false);
  const [newViewDialogOpen, setNewViewDialogOpen] = useState(false);

  useEffect(() => {
    apiClient.get('/data-items')
      .then(response => {
        const list: any[] = response.data;
        const map: Record<string, any> = {};
        list.forEach(item => { map[item.data_index] = item; });
        setItemCatalog(map);
      })
      .catch(console.error);
  }, []);

  const itemCatalogKeys = useMemo(() => Object.keys(itemCatalog), [itemCatalog]);

  // Check OCI availability on mount
  useEffect(() => {
    const checkOCI = async () => {
      try {
        const response = await apiClient.get('/view-definition/oci/check');
        setOciAvailable(response.data.available);
      } catch (error) {
        console.error('Failed to check OCI availability:', error);
        setOciAvailable(false);
      }
    };
    checkOCI();
  }, []);

  // ────────────────────────────────────────────────────────
  // 1️⃣ Load navigation rows only once
  useEffect(() => {
    apiClient.get('/navigation')
      .then(response => {
        const data: NavigationRow[] = response.data;
        setUIConfig(data);
      })
      .catch(console.error);
  }, []);  // ← only on mount

  // When the user clicks a viewLabel in the sidebar (which sets leftNav AND view
  // atomically), this ref tells the leftNav cleanup useEffect "the handler has
  // already set selectedView correctly — don't clobber it." Without this, the
  // cleanup runs after the state commit and resets selectedView back to ''.
  const programmaticNavRef = useRef(false);
  // Same intent for the view-level useEffect: when a handler atomically sets
  // BOTH selectedView and selectedConfig (handleConfigSelect), prevent the
  // view useEffect from wiping config. Without this, the first click on a
  // config item showed the matrix briefly because config got cleared.
  const programmaticViewRef = useRef(false);

  // 2️⃣ / 3️⃣ / 4️⃣ — cascading "clear downstream on change" effects.
  //
  // Use a "last stable value" ref instead of a "first mount" guard. React 18
  // + StrictMode double-mounts in dev: a useRef(true) guard would fire the
  // reset on the SECOND mount and wipe persisted/restored values. The stable
  // ref pattern just compares: if the observed value equals the last value
  // we processed, this is a re-render/re-mount, not a real change — return.
  const stableTopRef = useRef(selectedTopNav);
  useEffect(() => {
    if (selectedTopNav === stableTopRef.current) return;
    stableTopRef.current = selectedTopNav;
    setSelectedLeftNav("");
    setSelectedView("");
    setSelectedConfig("");
    setViewData(null);
    setRootProps({});
    setUnsavedData(null);
    setUnsavedRootProps(null);
    setUnsavedTdOnlyMap({});
    setIsDirty(false);
  }, [selectedTopNav]);

  const stableLeftRef = useRef(selectedLeftNav);
  useEffect(() => {
    if (selectedLeftNav === stableLeftRef.current) return;
    stableLeftRef.current = selectedLeftNav;
    setViewData(null);
    setRootProps({});
    setUnsavedData(null);
    setUnsavedRootProps(null);
    setUnsavedTdOnlyMap({});
    setIsDirty(false);
    if (programmaticNavRef.current) {
      programmaticNavRef.current = false;
      return;
    }
    setSelectedView("");
    setSelectedConfig("");
  }, [selectedLeftNav]);

  const stableViewRef = useRef(selectedView);
  useEffect(() => {
    if (selectedView === stableViewRef.current) return;
    stableViewRef.current = selectedView;
    if (programmaticViewRef.current) {
      programmaticViewRef.current = false;
      return;
    }
    setSelectedConfig("");
    setViewData(null);
    setRootProps({});
    setUnsavedData(null);
    setUnsavedRootProps(null);
    setUnsavedTdOnlyMap({});
    setIsDirty(false);
  }, [selectedView]);

  // 5️⃣ Load the view from Postgres API whenever all four selectors are set
  useEffect(() => {
    if (
      selectedTopNav &&
      selectedLeftNav &&
      selectedView &&
      selectedConfig
    ) {
      // Bug 1 root cause: config_ui_navigation stores ONE row per parent→child
      // view reference. The `config` column is the parent context's view name;
      // `view_name` is the actual file to fetch and render. For a tabs-based
      // view like Release Allocations, there can be multiple rows under the
      // same (topNav, leftNav, viewLabel) — e.g. config=Worklist→view_name=View
      // and config=View→view_name=LifecycleTab. Joining on `config` returned
      // the wrong row and fetched the referenced (child) view's content
      // instead of the one the user intended. We now identify the user's
      // selection by `view_name` (= viewKey), which is unique within a nav
      // coordinate.
      const matchingRow = uiConfig.find(r =>
        r.topNav === selectedTopNav &&
        r.leftNav === selectedLeftNav &&
        r.viewLabel === selectedView &&
        r.viewKey === selectedConfig
      );
      if (!matchingRow) {
        console.error("No matching navigation row for", {
          selectedTopNav, selectedLeftNav, selectedView, selectedConfig
        });
        setViewData(null);
        return;
      }

      // Update TD/BU from matching row
      setIsTopDown(!!matchingRow.topDown);
      setIsBottomUp(!!matchingRow.bottomUp);

      apiClient.get(`/view-definition/${matchingRow.viewKey}`)
        .then(response => {
          const { items, viewType: vt, nodeField: nf, rootProps: rp, lastModifiedAt: lma } = response.data;
          // Normalize empty-string viewType to null — only a real source `type`
          // field should produce a `type:` key in the rendered JSON.
          setViewType(vt !== undefined && vt !== '' ? vt : null);
          setNodeField(nf !== undefined ? nf : null);
          setRootProps(rp);
          setDerivedViewName(matchingRow.viewKey);
          setViewData(items);
          setLastModifiedAt(lma);
          setUnsavedData(items);
          setIsDirty(false);
        })
        .catch(err => {
          console.error(err);
          setViewData(null);
        });
    }
  }, [
    selectedTopNav,
    selectedLeftNav,
    selectedView,
    selectedConfig,
    uiConfig
  ]);

  /* build payload for POST */
  const buildSavePayload = () => ({
    rootProps: unsavedRootProps ?? rootProps,
    items: unsavedData ?? viewData,
    lastModifiedAt: lastModifiedAt,
    viewType,
    nodeField,
  });

  /* central handler: called by every editor when something changes */
  const markDirty = useCallback((rows: ViewItem[], tdMap: Record<string, boolean>, rp = rootProps) => {
    setUnsavedData(rows);
    setUnsavedRootProps(rp);
    setUnsavedTdOnlyMap(tdMap);
    setIsDirty(true);
  }, [rootProps]);

  // New: handle editing of rootProps in the props‐only editor
  const handleRootPropsChange = useCallback((newRootProps: Record<string, any>) => {
    setRootProps(newRootProps);
    setUnsavedRootProps(newRootProps);
    // re-use the same dirty payload (items stay the same)
    markDirty(unsavedData ?? viewData ?? [], unsavedTdOnlyMap, newRootProps);
  }, [markDirty, unsavedData, viewData, unsavedTdOnlyMap]);

  const handleRowsChange = (rows: ViewItem[]) => {
    console.log('DEBUG handleRowsChange: Received rows with label_text:', rows.map(r => ({ item_id: r.item_id, label_text: r.label_text })));
    // 1) Immediately update the UI
    setViewData(rows);
    // 2) Mark "dirty" so your Save button knows there are changes
    markDirty(rows, unsavedTdOnlyMap);
  };

  /* update callbacks passed to DynamicForm ------------------------- */
  const handleViewDataChange = (rows: ViewItem[]) => markDirty(rows, unsavedTdOnlyMap);
  const handleDirtyChange = (rows: ViewItem[], tdMap: Record<string, boolean>) => {
    markDirty(rows, tdMap);
  };

  // Recursive function to filter data in App.js.
  const filterData = (data: any, tdMap: Record<string, boolean>, currentPath = ""): any => {
    if (typeof data !== "object" || data === null) return data;

    if (Array.isArray(data)) {
      return data
        .map((item, index) => {
          const keyPath = currentPath ? `${currentPath}.${index}` : `${index}`;
          if (tdMap[keyPath] === true) {
            return undefined;
          }
          return filterData(item, tdMap, keyPath);
        })
        .filter((item) => item !== undefined);
    }

    const result: any = {};
    Object.keys(data).forEach((key) => {
      const keyPath = currentPath ? `${currentPath}.${key}` : key;
      if (tdMap[keyPath] === true) return; // skip this key if checked
      result[key] = filterData(data[key], tdMap, keyPath);
    });
    return result;
  };

  const sanitizeData = (data: any): any => {
    if (Array.isArray(data)) {
      return data.map(item => sanitizeData(item)); // Recursively sanitize each item in the array
    } else if (typeof data === 'object' && data !== null) {
      const { _isNew, ...sanitized } = data; // Destructure to remove _isNew
      Object.keys(sanitized).forEach(key => {
        const value = sanitized[key];

        // Handle empty strings based on field type
        if (value === '') {
          // Integer fields - convert empty to proper default
          if (key === 'seq_no') {
            sanitized[key] = 0;
          }
          // Nullable integer/numeric fields
          else if (key === 'width' || key === 'pinned') {
            sanitized[key] = null;
          }
          // String fields with defaults
          else if (key === 'role') {
            sanitized[key] = 'leaf';
          }
          // All other empty strings become null (safer for database)
          else if (key !== 'item_id' && key !== 'view_name') {
            sanitized[key] = null;
          }
        } else {
          // Recursively sanitize nested objects
          sanitized[key] = sanitizeData(value);
        }
      });
      return sanitized;
    }
    return data; // Return primitive values as is
  };

  // Save JSON button handler.
  const saveData = async () => {
    // Produce the filtered JSON by excluding keys where the TD Only checkbox is checked.
    const filteredJson = filterData(unsavedData, unsavedTdOnlyMap);

    // Sanitize both filteredJson and fullJson to remove _isNew
    const sanitizedFilteredJson = sanitizeData(filteredJson);
    const sanitizedFullJson = sanitizeData(unsavedData);

    // Build payload with SANITIZED data. viewType and nodeField are sent
    // explicitly so the server doesn't have to fabricate them — that was
    // the source of Bug 4 (server falling back to viewType='grid').
    const payload = {
      rootProps: unsavedRootProps ?? rootProps,
      items: sanitizedFullJson,  // Use sanitized data, not raw unsavedData!
      viewType,
      nodeField
    };

    // Debug: Log the payload to see what we're sending
    console.log('Saving payload:', payload);
    console.log('Sanitized items:', sanitizedFullJson);

    // Check for any remaining empty strings in critical fields
    if (sanitizedFullJson && Array.isArray(sanitizedFullJson)) {
      sanitizedFullJson.forEach((item: any, index: number) => {
        if (item.seq_no === '') console.warn(`Row ${index} has empty seq_no`);
        if (item.width === '') console.warn(`Row ${index} has empty width`);
        if (item.pinned === '') console.warn(`Row ${index} has empty pinned`);
      });
    }

    // Inner helper so we can recursively retry with variant_choices once the
    // user opts in (Bug 3).
    const tryPost = async (variantChoices: Record<string, any> | undefined): Promise<boolean> => {
      const body = variantChoices && Object.keys(variantChoices).length > 0
        ? { ...payload, variant_choices: variantChoices }
        : payload;
      try {
        await apiClient.post(`/view-definition/${derivedViewName}`, body);
        return true;
      } catch (err: any) {
        const status = err?.response?.status;
        const data = err?.response?.data;

        // 409 — optimistic-lock conflict (someone else updated the view).
        // axios throws on 4xx so the old `r.status === 409` path was dead;
        // this is the live one.
        if (status === 409) {
          const shouldRefresh = window.confirm(
            `Conflict detected: ${data?.message || 'view was modified'}\n\n` +
            'This view has been modified by another user. Your changes will be lost if you refresh.\n\n' +
            'Click OK to refresh and lose your changes, or Cancel to keep editing.'
          );
          if (shouldRefresh) window.location.reload();
          return false;
        }

        // 400 with needs_variant — the user added a data_index that isn't in
        // data_item_catalog. The catalog invariant requires every referenced
        // data_index to have a catalog row. Offer to create new placeholder
        // catalog entries (source='new'), then retry the same save.
        if (status === 400 && Array.isArray(data?.needs_variant) && data.needs_variant.length > 0) {
          const list = data.needs_variant.join('\n  • ');
          const ok = window.confirm(
            `These data_index value(s) aren't in the catalog yet:\n\n  • ${list}\n\n` +
            `Create them as new catalog entries and save? You can edit each one's label / formula / renderer in the Data Item Catalog later.`
          );
          if (!ok) {
            // User cancelled — keep edits in-memory, dirty stays true.
            return false;
          }
          const newChoices: Record<string, any> = { ...(variantChoices || {}) };
          for (const di of data.needs_variant) {
            newChoices[di] = { source: 'new', item_type: 'computed_metric' };
          }
          return tryPost(newChoices);
        }

        // Anything else — surface the structured error if present.
        console.error('Save error:', err);
        const detail = data?.details || data?.message || data?.error;
        alert(`Save failed: ${detail || err.message}`);
        return false;
      }
    };

    const ok = await tryPost(undefined);
    if (!ok) return;

    alert('Saved to database!');
    setIsDirty(false);
    const now = new Date().toISOString();
    setLastModifiedAt(now);
  };

  // Warn user about unsaved changes on unload.
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isDirty]);

  async function handleExport() {
    if (!viewData) return;

    // Helper: recursively strip item_id and parent_item_id from nodes
    function stripIds(node: any) {
      delete node.item_id;
      delete node.parent_item_id;
      if (Array.isArray(node.view)) node.view.forEach(stripIds);
    }

    // Helper: recursively filter out TD-only rows from the tree
    const filterTreeForTDOnly = (nodes: any[], tdOnlyMap: Record<string, boolean>, currentPath = '') => {
      return nodes
        .filter(node => {
          const pathKey = currentPath ? `${currentPath}.${node.item_id}` : node.item_id;
          // Exclude node if marked as TD-only
          return !tdOnlyMap[pathKey];
        })
        .map(node => ({
          ...node,
          view: node.view ? filterTreeForTDOnly(node.view, tdOnlyMap, `${currentPath}.${node.item_id}`) : undefined
        }));
    };

    // Build the full data (all records). buildViewdefnJson places the
    // column tree under the source's original key (Bug 3), omits `type`
    // when viewType is null (Bug 4), and preserves rootProps key order
    // (Bug 5). cleanObject only strips undefined now (Bug 2).
    const treeFull = buildView(viewData);
    treeFull.forEach(stripIds);
    const fullData = cleanObject(buildViewdefnJson(rootProps, viewType, nodeField, treeFull));

    // Build the filtered data (exclude TD-only checked rows)
    const hasFiltered = Object.values(unsavedTdOnlyMap).some(Boolean);
    let filteredData;

    if (hasFiltered) {
      const filteredTree = filterTreeForTDOnly(treeFull, unsavedTdOnlyMap);
      filteredTree.forEach(stripIds);
      filteredData = cleanObject(buildViewdefnJson(rootProps, viewType, nodeField, filteredTree));
    } else {
      // If no TD-only checks, filtered = full
      filteredData = fullData;
    }

    // File names setup
    const nameFull = `TD_${selectedConfig}.viewdefn`;
    const nameFiltered = `${selectedConfig}.viewdefn`;

    // Create blobs
    const blobFull = new Blob([JSON.stringify(fullData, null, 2)], { type: "application/json" });
    const blobFiltered = new Blob([JSON.stringify(filteredData, null, 2)], { type: "application/json" });

    // Download full TD file always only if hasFiltered true
    if (hasFiltered) {
      const url = URL.createObjectURL(blobFull);
      const a = document.createElement("a");
      a.href = url;
      a.download = nameFull;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    }

    // Download filtered file always
    {
      const url = URL.createObjectURL(blobFiltered);
      const a = document.createElement("a");
      a.href = url;
      a.download = nameFiltered;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    }
  }

  // Handler for syncing to OCI
  const handleSyncToOCI = useCallback(async () => {
    if (!viewData || !selectedConfig) return;

    if (!window.confirm('This will save all pending changes and sync the view definition to OCI. Continue?')) {
      return;
    }

    try {
      setOciSyncing(true);

      // Save pending changes first if any
      if (isDirty) {
        await saveData();
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      // Build the preview data (same as what's shown in preview)
      const tree = buildView(viewData);

      function stripIds(node: any) {
        delete node.item_id;
        delete node.parent_item_id;
        if (Array.isArray(node.view)) node.view.forEach(stripIds);
      }
      tree.forEach(stripIds);

      const cleanedView = cleanObject(tree);
      const viewJson = buildViewdefnJson(rootProps, viewType, nodeField, cleanedView);

      // Sync to OCI - just send the JSON and viewKey
      const response = await apiClient.post(`/view-definition/${encodeURIComponent(selectedConfig)}/sync-to-oci`, viewJson);

      if (response.data.success) {
        alert('View definition synced to OCI successfully!');
      } else {
        throw new Error(response.data.error || 'Sync failed');
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.details || err.response?.data?.error || err.message || 'Failed to sync to OCI';
      alert(`Sync failed: ${errorMessage}`);
      console.error('OCI sync error:', err);
    } finally {
      setOciSyncing(false);
    }
  }, [viewData, selectedConfig, isDirty, viewType, rootProps, saveData]);

  // Excel download handler
  const handleDownloadExcel = useCallback(async () => {
    if (!selectedConfig) {
      alert('Please select a view first');
      return;
    }

    try {
      const response = await apiClient.get(`/view-definition/${encodeURIComponent(selectedConfig)}/export-excel`, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${selectedConfig}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to download Excel';
      alert(`Download failed: ${errorMessage}`);
      console.error('Excel download error:', err);
    }
  }, [selectedConfig]);

  // Excel upload handler
  const handleUploadExcel = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !selectedConfig) return;

    if (!window.confirm('This will replace the current view definition with data from the Excel file. Continue?')) {
      // Reset file input
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    try {
      setUploadingExcel(true);

      const formData = new FormData();
      formData.append('file', file);

      const response = await apiClient.post(`/view-definition/${encodeURIComponent(selectedConfig)}/import-excel`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        alert(`Successfully imported ${response.data.itemsImported} items!`);
        // Reload the view data by triggering the useEffect
        const currentConfig = selectedConfig;
        setSelectedConfig('');
        setTimeout(() => setSelectedConfig(currentConfig), 100);
      } else {
        throw new Error(response.data.error || 'Import failed');
      }
    } catch (err: any) {
      const errorDetails = err.response?.data?.details;
      const errorMessage = err.response?.data?.error || err.message || 'Failed to upload Excel';

      if (Array.isArray(errorDetails)) {
        alert(`Import failed:\n\n${errorDetails.join('\n')}`);
      } else {
        alert(`Import failed: ${errorMessage}`);
      }
      console.error('Excel upload error:', err);
    } finally {
      setUploadingExcel(false);
      // Reset file input
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [selectedConfig]);

  /* ───── Navigation data derivation ───── */
  const topNavTabs = useMemo(
    () => [...new Set(uiConfig.map(r => r.topNav).filter(Boolean))],
    [uiConfig]
  );

  // 3-level sidebar data: leftNav -> { viewLabel -> viewKey[] }
  //
  // Listing viewKey (= view_name) not configName (= config column / parent
  // context). With configName, the sidebar would show parent-references like
  // "companionView" and hide referenced children like
  // "AssortmentLinePlanLifecycleTab" (which is referenced from inside other
  // views but has no row whose config column equals its own name). Listing by
  // viewKey shows the actual viewdefn files that exist for this nav slot, and
  // it's what the matching join below now uses.
  const sidebarData = useMemo(() => {
    const filtered = uiConfig.filter(r => r.topNav === selectedTopNav);
    const grouped: Record<string, Record<string, string[]>> = {};
    filtered.forEach(r => {
      if (!grouped[r.leftNav]) grouped[r.leftNav] = {};
      if (!grouped[r.leftNav][r.viewLabel]) grouped[r.leftNav][r.viewLabel] = [];
      if (!grouped[r.leftNav][r.viewLabel].includes(r.viewKey)) {
        grouped[r.leftNav][r.viewLabel].push(r.viewKey);
      }
    });
    return grouped;
  }, [uiConfig, selectedTopNav]);

  // Per-view metadata for the sidebar: keyed by `${leftNav}|${viewLabel}|${viewKey}`.
  // Same identifier as the sidebar list above and the lookup below.
  const viewMeta = useMemo(() => {
    const map: Record<string, { viewType?: string | null; itemCount?: number | null }> = {};
    uiConfig
      .filter(r => r.topNav === selectedTopNav)
      .forEach(r => {
        const key = `${r.leftNav}|${r.viewLabel}|${r.viewKey}`;
        const count = r.itemCount === null || r.itemCount === undefined
          ? null
          : (typeof r.itemCount === 'string' ? parseInt(r.itemCount, 10) : r.itemCount);
        map[key] = { viewType: r.viewType, itemCount: Number.isFinite(count as number) ? (count as number) : null };
      });
    return map;
  }, [uiConfig, selectedTopNav]);

  // Handle sidebar config selection — sets all 3 levels at once. Both refs
  // are set so the leftNav-level AND view-level cascading useEffects skip
  // wiping selectedView / selectedConfig. Without programmaticViewRef, the
  // first click on a config showed the matrix briefly because the view
  // useEffect cleared config-as-side-effect.
  const handleConfigSelect = useCallback((leftNav: string, view: string, config: string) => {
    programmaticNavRef.current = true;
    programmaticViewRef.current = true;
    setSelectedLeftNav(leftNav);
    setSelectedView(view);
    setSelectedConfig(config);
  }, []);

  /* ───── Preview data prep ───── */
  const currentRows = unsavedData ?? viewData;
  let previewData = null;
  if (currentRows) {
    const tree = buildView(currentRows);

    function stripIds(node: any) {
      delete node.item_id;
      delete node.parent_item_id;
      if (Array.isArray(node.view)) node.view.forEach(stripIds);
    }
    tree.forEach(stripIds);

    const cleanedView = cleanObject(tree);

    previewData = buildViewdefnJson(rootProps, viewType, nodeField, cleanedView);
  }

  // Selected row for detail panel
  const selectedRow = useMemo(() => {
    if (!selectedRowId || !viewData) return null;
    return viewData.find(r => r.item_id === selectedRowId) || null;
  }, [selectedRowId, viewData]);

  /* ───── Handle upload button click (hidden file input) ───── */
  const handleUploadClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  /* ───────────────────── RENDER ─────────────────────── */
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      {/* Top Navigation Bar */}
      <TopNavBar
        tabs={topNavTabs}
        activeTab={selectedTopNav}
        onTabChange={setSelectedTopNav}
      />

      {/* Sub-tab strip: Columns | Models | Pivots. Models + Pivots need a
          selected view (they read per-view relationships from
          view_model_pivot_mapping); Columns is always reachable so the
          user is never stranded on a disabled tab. */}
      <ViewSubTabs
        value={activeSubTab}
        onChange={setActiveSubTab}
        hasView={Boolean(derivedViewName)}
      />

      {activeSubTab === 'models' ? (
        <Box sx={{ flex: 1, overflow: 'hidden' }}>
          {derivedViewName ? (
            <ModelsPanel viewName={derivedViewName} />
          ) : (
            <Box sx={{ p: 3, color: 'text.secondary' }}>Pick a view in the left nav to see its models.</Box>
          )}
        </Box>
      ) : activeSubTab === 'pivots' ? (
        <Box sx={{ flex: 1, overflow: 'hidden' }}>
          {derivedViewName ? (
            <PivotsPanel viewName={derivedViewName} />
          ) : (
            <Box sx={{ p: 3, color: 'text.secondary' }}>Pick a view in the left nav to see its pivots.</Box>
          )}
        </Box>
      ) : (
      <>
      {/* Context Breadcrumb */}
      <ContextBar
        topNav={selectedTopNav}
        section={selectedLeftNav}
        view={selectedView || selectedConfig}
        columnCount={viewData?.length ?? 0}
        isDirty={isDirty}
      />

      {/* Main content area */}
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Left Sidebar */}
        <LeftSidebar
          navData={sidebarData}
          viewMeta={viewMeta}
          activeSection={selectedLeftNav}
          onSectionChange={(leftNav) => {
            // Reset deeper selections so the matrix scopes to the leftNav level.
            // Explicitly clear viewData/rootProps too — don't rely on useEffect
            // timing — so the right-pane conditional reliably picks the matrix.
            setSelectedLeftNav(leftNav);
            setSelectedView('');
            setSelectedConfig('');
            setViewData(null);
            setRootProps({});
            setUnsavedData(null);
            setUnsavedRootProps(null);
            setUnsavedTdOnlyMap({});
            setIsDirty(false);
          }}
          activeView={selectedView}
          activeConfig={selectedConfig}
          onConfigSelect={handleConfigSelect}
          onViewLabelSelect={(leftNav, view) => {
            // Selecting a viewLabel folder narrows the matrix to that folder's configs.
            // Mark this as a programmatic navigation so the leftNav cleanup useEffect
            // doesn't clobber the selectedView we just set. Also explicitly clear
            // editor state for deterministic right-pane rendering.
            programmaticNavRef.current = true;
            setSelectedLeftNav(leftNav);
            setSelectedView(view);
            setSelectedConfig('');
            setViewData(null);
            setRootProps({});
            setUnsavedData(null);
            setUnsavedRootProps(null);
            setUnsavedTdOnlyMap({});
            setIsDirty(false);
          }}
          onNewView={selectedTopNav ? () => setNewViewDialogOpen(true) : undefined}
          collapsed={sidebarCollapsed}
          onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        />

        {/* Center content */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* View Title Bar with mode toggle and action buttons */}
          <ViewTitleBar
            viewName={selectedView || selectedConfig || 'Select a view'}
            mode={viewMode}
            onModeChange={setViewMode}
            onSave={saveData}
            onExport={handleExport}
            onDownloadExcel={handleDownloadExcel}
            onUploadExcel={handleUploadClick}
            onSyncOCI={handleSyncToOCI}
            onToggleChat={() => setChatOpen(!chatOpen)}
            isDirty={isDirty}
            ociAvailable={ociAvailable}
            ociSyncing={ociSyncing}
            uploadingExcel={uploadingExcel}
          />

          {/* Hidden file input for Excel upload */}
          <input
            ref={fileInputRef}
            type="file"
            hidden
            accept=".xlsx,.xls"
            onChange={handleUploadExcel}
          />

          {/* Mode Indicator (Top-Down / Bottom-Up) */}
          <ModeIndicator
            isTopDown={isTopDown}
            isBottomUp={isBottomUp}
            onToggleTopDown={() => setIsTopDown(!isTopDown)}
            viewType={viewType}
          />

          {/* Editor / JSON / Preview content area */}
          <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
            {/* Main editor area */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
              {viewMode === 'grid' && (
                <>
                  {/* Right-pane mode logic (precedence):
                        1. config selected AND view data loaded → per-view editor
                        2. leftNav (or deeper) selected → coverage matrix scoped to it
                        3. only top-nav selected → "select a section" placeholder
                        4. nothing selected → initial placeholder
                      Note: gates on selectedConfig (not rootProps) so toggling between
                      a leaf and a folder reliably switches the right pane. */}
                  {selectedConfig ? (
                    viewData && viewData.length > 0 ? (
                      <DynamicForm
                        viewRows={viewData}
                        allowTDOnly={allowTDOnly}
                        rootProps={rootProps}
                        onDataChange={handleRowsChange}
                        onRootChange={handleRootPropsChange}
                        viewType={viewType}
                        isTopDown={isTopDown}
                        onDirtyChange={handleDirtyChange}
                        itemCatalog={itemCatalog}
                        itemCatalogKeys={itemCatalogKeys}
                        readOnly={true}
                      />
                    ) : (
                      // Props-only editor for views with no items (rootProps loaded but no rows)
                      <Box sx={{ p: 2 }}>
                        <DynamicForm
                          viewRows={[]}
                          rootProps={rootProps}
                          onDataChange={handleViewDataChange}
                          onRootChange={handleRootPropsChange}
                          itemCatalog={itemCatalog}
                          itemCatalogKeys={itemCatalogKeys}
                          allowTDOnly={false}
                          readOnly={true}
                        />
                      </Box>
                    )
                  ) : selectedLeftNav ? (
                    // Matrix renders when a sidebar folder is selected:
                    //   - leftNav only → matrix scoped to that leftNav's views
                    //   - leftNav + viewLabel → matrix scoped to that viewLabel's configs
                    <CoverageMatrix
                      section={selectedTopNav}
                      leftNav={selectedLeftNav}
                      viewLabel={selectedView || null}
                      onSavedRefresh={() => {
                        apiClient.get('/navigation').then(r => setUIConfig(r.data)).catch(console.error);
                      }}
                      onClose={() => {
                        setSelectedLeftNav('');
                        setSelectedView('');
                        setSelectedConfig('');
                        setViewData(null);
                        setRootProps({});
                      }}
                    />
                  ) : selectedTopNav ? (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                      <Typography variant="body1" color="text.secondary">
                        Select a section in the sidebar (e.g. <strong>HISTORY GRID</strong>) to see its coverage matrix,
                        or expand a section and click a view to load it.
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                      <Typography variant="body1" color="text.secondary">
                        Please select a section from the top navigation to begin.
                      </Typography>
                    </Box>
                  )}

                  {/* Root Props collapsible */}
                  {Object.keys(rootProps).length > 0 && (
                    <Box sx={{ borderTop: '1px solid #E0E0E0' }}>
                      <Box
                        onClick={() => setRootPropsExpanded(!rootPropsExpanded)}
                        sx={{
                          p: '8px 16px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          background: '#FAFAFA',
                          '&:hover': { background: '#F0F0F0' },
                          fontSize: 12,
                          fontWeight: 600,
                          color: '#555',
                        }}
                      >
                        {rootPropsExpanded ? '▾' : '▸'} Root Properties
                      </Box>
                      <Collapse in={rootPropsExpanded}>
                        <Paper
                          variant="outlined"
                          sx={{
                            m: 1,
                            p: 2,
                            backgroundColor: '#f5f5f5',
                            maxHeight: 200,
                            overflow: 'auto',
                          }}
                        >
                          <pre style={{ fontFamily: 'monospace', fontSize: 12, margin: 0 }}>
                            {JSON.stringify(rootProps, null, 2)}
                          </pre>
                        </Paper>
                      </Collapse>
                    </Box>
                  )}
                </>
              )}

              {viewMode === 'json' && (
                <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      backgroundColor: '#1e1e1e',
                      minHeight: '100%',
                    }}
                  >
                    <pre style={{
                      fontFamily: '"Fira Code", "Consolas", monospace',
                      fontSize: 12,
                      color: '#d4d4d4',
                      margin: 0,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}>
                      {previewData ? JSON.stringify(previewData, null, 2) : 'No data loaded'}
                    </pre>
                  </Paper>
                </Box>
              )}

              {viewMode === 'preview' && (
                <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                  {viewData && viewData.length > 0 ? (
                    <Box>
                      {/* Group header row */}
                      <Box sx={{ display: 'flex', borderBottom: '2px solid #004D40' }}>
                        {viewData.filter(r => r.role === 'group' && !r.parent_item_id).map(group => {
                          const children = viewData.filter(r => r.parent_item_id === group.item_id);
                          return (
                            <Box
                              key={group.item_id}
                              sx={{
                                flex: children.length || 1,
                                textAlign: 'center',
                                p: '6px 4px',
                                background: '#E8F5E9',
                                borderRight: '1px solid #C8E6C9',
                                fontSize: 11,
                                fontWeight: 700,
                                color: '#2E7D32',
                                textTransform: 'uppercase',
                                letterSpacing: 0.5,
                              }}
                            >
                              {group.label_text}
                            </Box>
                          );
                        })}
                      </Box>
                      {/* Column header row */}
                      <Box sx={{ display: 'flex', borderBottom: '1px solid #E0E0E0' }}>
                        {viewData.filter(r => r.role !== 'group').map(col => (
                          <Box
                            key={col.item_id}
                            sx={{
                              minWidth: col.width || 80,
                              maxWidth: col.width || 120,
                              p: '8px 6px',
                              background: col.pinned ? '#FFF9C4' : '#FAFAFA',
                              borderRight: '1px solid #EEE',
                              fontSize: 11,
                              fontWeight: 600,
                              color: '#333',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {col.label_text || col.data_index}
                          </Box>
                        ))}
                      </Box>
                      <Typography variant="caption" sx={{ display: 'block', mt: 2, color: '#999', textAlign: 'center' }}>
                        Column layout preview — {viewData.filter(r => r.role !== 'group').length} columns
                      </Typography>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 4 }}>
                      No view data to preview
                    </Typography>
                  )}
                </Box>
              )}
            </Box>

            {/* Column Detail Panel (right side) */}
            {viewMode === 'grid' && selectedRow && (
              <ColumnDetailPanel
                row={selectedRow}
                onClose={() => setSelectedRowId(null)}
              />
            )}
          </Box>
        </Box>
      </Box>
      </>
      )}

      {/* Chatbot Drawer */}
      <Drawer
        anchor="right"
        open={chatOpen}
        variant="persistent"
        sx={{
          width: 450,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: 450,
            boxSizing: 'border-box'
          }
        }}
      >
        <ChatbotPanel
          currentView={selectedView || selectedConfig}
          currentViewId={derivedViewName}
          onClose={() => setChatOpen(false)}
        />
      </Drawer>

      {/* New View dialog (Phase 2) */}
      <NewViewDialog
        open={newViewDialogOpen}
        topNav={selectedTopNav}
        onClose={() => setNewViewDialogOpen(false)}
        onCreated={(viewKey) => {
          setNewViewDialogOpen(false);
          // Refetch navigation so the new view appears in the sidebar.
          apiClient.get('/navigation').then(r => setUIConfig(r.data)).catch(console.error);
          console.log('Created view:', viewKey);
        }}
      />
    </Box>
  );

}
export default ViewManager;
